import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from "../Assets/Images/logo.png";
import { formatNumbers } from "./Common";

async function loadLogoDataUrl(): Promise<string | null> {
  try {
    const response = await fetch(Logo);
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** Build a Petty Cash Voucher PDF from a requisition (or voucher-shaped) record. */
export async function buildPettyCashVoucherPdf(detail: any = {}) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 15;
  const contentW = pageW - marginX * 2;
  const items = Array.isArray(detail.items)
    ? detail.items
    : Array.isArray(detail.particulars)
      ? detail.particulars
      : [];
  const total =
    Number(detail.grand_total) ||
    items.reduce(
      (sum: number, item: any) => sum + (Number(item?.amount) || 0),
      0
    );

  const seriesNo = detail.pc_no || detail.series_no || detail.id || "";
  const paidTo = detail.supplier_name || "";
  const position = detail.position || "";
  const voucherDate = dayjs(detail.date || undefined).format("MMMM D, YYYY");

  const logoDataUrl = await loadLogoDataUrl();
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, "PNG", marginX, 10, 18, 18);
    } catch {
      // skip logo
    }
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("CEBU AGUA LAB, INC.", pageW / 2, 16, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(
    "ADG IT Center 6th Floor, G. Lopez Jaena St., Subangdaku, Mandaue City",
    pageW / 2,
    21,
    { align: "center" }
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Series No", pageW - marginX - 28, 14);
  doc.setTextColor(200, 0, 0);
  doc.setFontSize(16);
  doc.text(String(seriesNo), pageW - marginX - 14, 22, { align: "center" });
  doc.setTextColor(0, 0, 0);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("PETTY CASH VOUCHER FORM", pageW / 2, 36, { align: "center" });

  const fieldY1 = 46;
  const fieldY2 = 54;
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("PAID TO:", marginX, fieldY1);
  doc.setFont("helvetica", "normal");
  doc.line(marginX + 24, fieldY1 + 1, marginX + 120, fieldY1 + 1);
  doc.text(String(paidTo), marginX + 25, fieldY1 - 1);

  doc.setFont("helvetica", "bold");
  doc.text("Date:", marginX + 125, fieldY1);
  doc.setFont("helvetica", "normal");
  doc.line(marginX + 140, fieldY1 + 1, pageW - marginX, fieldY1 + 1);
  doc.text(voucherDate, marginX + 141, fieldY1 - 1);

  doc.setFont("helvetica", "bold");
  doc.text("POSITION:", marginX, fieldY2);
  doc.setFont("helvetica", "normal");
  doc.line(marginX + 28, fieldY2 + 1, marginX + 120, fieldY2 + 1);
  doc.text(String(position), marginX + 29, fieldY2 - 1);

  const minRows = 6;
  const tableBody = (
    items.length
      ? items
      : [{ particulars: "Petty cash expense", amount: total }]
  ).map((item: any, index: number) => [
    String(index + 1),
    item.particulars || item.item_name || "",
    formatNumbers(Number(item.amount) || 0),
  ]);

  while (tableBody.length < minRows) {
    tableBody.push(["", "", ""]);
  }
  tableBody.push(["", "TOTAL =>", formatNumbers(total)]);

  autoTable(doc, {
    startY: 60,
    margin: { left: marginX, right: marginX },
    head: [
      [
        { content: "NO.", styles: { halign: "center" } },
        { content: "PARTICULARS", styles: { halign: "center" } },
        { content: "AMOUNT", styles: { halign: "center" } },
      ],
    ],
    body: tableBody,
    theme: "grid",
    styles: {
      fontSize: 9,
      cellPadding: 2.5,
      lineColor: [0, 0, 0],
      lineWidth: 0.3,
      minCellHeight: 8,
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 14, halign: "center" },
      1: { cellWidth: contentW - 14 - 35 },
      2: { cellWidth: 35, halign: "right" },
    },
    didParseCell: (data) => {
      if (
        data.section === "body" &&
        data.row.index === tableBody.length - 1
      ) {
        data.cell.styles.fontStyle = "bold";
        if (data.column.index === 1) data.cell.styles.halign = "right";
      }
    },
  });

  const lastY = (doc as any).lastAutoTable?.finalY || 140;
  const sigY = Math.max(lastY + 16, 190);
  const colW = contentW / 2 - 5;

  const drawSig = (
    x: number,
    y: number,
    label: string,
    name: string,
    sub: string
  ) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(label, x, y);
    doc.line(x, y + 12, x + colW, y + 12);
    if (name) {
      doc.setFontSize(8);
      doc.text(String(name), x + colW / 2, y + 10, { align: "center" });
    }
    doc.setFontSize(8);
    doc.text(sub, x + colW / 2, y + 16, { align: "center" });
  };

  drawSig(
    marginX,
    sigY,
    "Received by:",
    detail.received_by || detail.requested_by || "",
    "Signature Over Printed Name"
  );
  drawSig(
    marginX + contentW / 2 + 5,
    sigY,
    "Prepared by:",
    detail.prepared_by || detail.added_by_name || "",
    "Name & Signature of Cash Custodian"
  );
  drawSig(
    marginX,
    sigY + 30,
    "Checked by:",
    detail.checked_by || detail.checked_by_name || "",
    "Name and Signature"
  );
  drawSig(
    marginX + contentW / 2 + 5,
    sigY + 30,
    "Noted by:",
    detail.noted_by || "",
    "Name and Signature"
  );
  drawSig(
    marginX,
    sigY + 60,
    "Approved by:",
    detail.approver_name || detail.approved_by_name || "",
    "Name and Signature"
  );

  return doc;
}

export async function downloadPettyCashVoucherPdf(detail: any) {
  const doc = await buildPettyCashVoucherPdf(detail);
  const fileId = detail?.pc_no || detail?.series_no || detail?.id || "download";
  doc.save(`petty-cash-voucher-${fileId}.pdf`);
}
